import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Input, Output, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { merge, Subscription } from 'rxjs';
import { AuthService } from '@app/core/services/auth.service';
import { LoginPopupService } from '@app/core/services/login-popup.service';
import {
  PropertyCardDotAnimator,
  isTouchGalleryDevice,
  PROPERTY_CARD_SWIPE_THRESHOLD_PX,
  propertyCardWindowedSlides,
  propertyCardWindowedTrackPercent,
  PropertyCardWindowedSlide,
  PropertyCardWindowedTrackPosition,
  wrapPropertyPhotoIndex,
} from '@app/utils/property-card-gallery.util';
import {
  PropertyCardStatSegment,
  buildPropertyStatsSegments,
  formatPropertyLocationLine,
  propertyCardFavoriteSvgPath,
  formatPropertyPrice,
  formatPropertyStreet,
  propertyCardImagePaths,
  propertyTypeFrom,
} from '@app/utils/property-card.util';
import { buildListingNavigation } from '@app/utils/listing-route.util';
import {
  ListingStatusBadge,
  resolveListingStatusBadge,
} from '@app/utils/listing-status.util';
import { PropertyRecord } from '@app/models/property.model';

@Component({
  standalone: true,
  imports: [CommonModule, NgOptimizedImage],
  selector: 'app-residential-card',
  templateUrl: './residential-card.component.html',
  styleUrls: ['./residential-card.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResidentialCardComponent implements OnInit, OnDestroy {
  @Input() propertyType?: string;
  @Input() property!: PropertyRecord;
  /** When set with buildingUnitCount > 1, card opens the building page instead of listing detail. */
  @Input() buildingSlug = '';
  @Input() buildingUnitCount = 0;
  /** Base path for building navigation, e.g. `/for-rent/building` or `/featured-properties/building`. */
  @Input() buildingRouteBase = '/for-rent/building';
  @Input() cardVariant: 'list' | 'favorites' = 'list';
  @Input() photoLoading: 'lazy' | 'eager' = 'lazy';
  /** When set, closing the listing detail returns to this path (e.g. favorites). */
  @Input() returnUrl?: string;
  @Output() favoriteToggled = new EventEmitter<{ listingId: string; favorite: boolean }>();

  @ViewChild('photoViewport') photoViewport?: ElementRef<HTMLElement>;
  @ViewChild('photoTrack') photoTrack?: ElementRef<HTMLElement>;

  currentIndex = 0;
  /** Set before first paint so mobile does not swap static LCP image → carousel. */
  readonly touchGallery = isTouchGalleryDevice();
  isPhotoDragging = false;
  photoDragOffset = 0;
  snapWithoutTransition = false;
  dotAnimator?: PropertyCardDotAnimator;
  private photoDragStartX = 0;
  private suppressClickUntil = 0;
  private authSubscription?: Subscription;
  private commitTrackPosition: PropertyCardWindowedTrackPosition | null = null;
  private pendingPhotoIndex: number | null = null;
  private pendingPreviousPhotoIndex: number | null = null;
  /** Preloads the incoming photo on the off-screen center slot before the track snaps back. */
  private centerSlidePreloadIndex: number | null = null;
  private snapFinalizeTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly authService: AuthService,
    public readonly loginPopup: LoginPopupService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const photoCount = this.photoCount;
    this.dotAnimator = new PropertyCardDotAnimator(photoCount, this.currentIndex, () =>
      this.cdr.markForCheck(),
    );

    this.authSubscription = merge(
      this.authService.isLoggedIn,
      this.authService.favoritesChanged,
    ).subscribe(() => this.cdr.markForCheck());
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
    this.dotAnimator?.destroy();
    this.clearSnapFinalizeTimer();
  }

  getPhotoTrackTransform(): string {
    if (!this.touchGallery || this.photoCount <= 1) {
      return 'translateX(0)';
    }

    const position = this.commitTrackPosition ?? 'center';
    const slidePercent = propertyCardWindowedTrackPercent(position);
    return `translateX(calc(${slidePercent}% + ${this.photoDragOffset}px))`;
  }

  get windowedPhotoSlides(): PropertyCardWindowedSlide[] {
    const slides = propertyCardWindowedSlides(this.currentIndex, this.photoCount);
    if (this.centerSlidePreloadIndex === null) {
      return slides;
    }

    return slides.map((slide) =>
      slide.role === 'current'
        ? { ...slide, photoIndex: this.centerSlidePreloadIndex }
        : slide,
    );
  }

  windowedSlideUrl(photoIndex: number | null): string {
    if (photoIndex === null) {
      return '';
    }

    return this.imagePaths[photoIndex] ?? '';
  }

  trackWindowedSlide(_index: number, slide: PropertyCardWindowedSlide): string {
    return slide.role;
  }

  windowedSlideLoading(photoIndex: number | null): 'eager' | 'lazy' {
    if (photoIndex === null) {
      return 'lazy';
    }

    const visibleIndex = this.centerSlidePreloadIndex ?? this.currentIndex;
    return this.photoLoading === 'eager' && photoIndex === visibleIndex ? 'eager' : 'lazy';
  }

  onPhotoTrackTransitionEnd(event: TransitionEvent): void {
    if (
      event.target !== event.currentTarget
      || event.propertyName !== 'transform'
      || this.commitTrackPosition === null
    ) {
      return;
    }

    const nextIndex = this.pendingPhotoIndex;
    const previousIndex = this.pendingPreviousPhotoIndex;

    if (nextIndex === null || previousIndex === null) {
      this.commitTrackPosition = null;
      this.pendingPhotoIndex = null;
      this.pendingPreviousPhotoIndex = null;
      return;
    }

    this.snapWithoutTransition = true;
    this.centerSlidePreloadIndex = nextIndex;
    this.cdr.markForCheck();
    this.queueCenterSlidePreloadCheck();
  }

  onCenterSlideLoad(): void {
    if (this.centerSlidePreloadIndex === null) {
      return;
    }

    this.finalizeWindowedSnap();
  }

  onPhotoDragStart(event: TouchEvent): void {
    if (this.photoCount <= 1) {
      return;
    }

    const target = event.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }

    this.isPhotoDragging = true;
    this.photoDragStartX = event.touches[0]?.clientX ?? 0;
    this.photoDragOffset = 0;
    this.syncDotsToPhotoIndex(this.currentIndex, false);
  }

  onPhotoDragMove(event: TouchEvent): void {
    if (!this.isPhotoDragging) {
      return;
    }

    const clientX = event.touches[0]?.clientX ?? 0;
    const delta = clientX - this.photoDragStartX;
    const atStart = this.currentIndex === 0 && delta > 0;
    const atEnd = this.currentIndex === this.photoCount - 1 && delta < 0;
    this.photoDragOffset = atStart || atEnd ? delta * 0.35 : delta;
    this.syncDotsToPhotoIndex(this.getDragPreviewPhotoIndex(), false);
    event.preventDefault();
    this.cdr.markForCheck();
  }

  onPhotoDragEnd(): void {
    if (!this.isPhotoDragging) {
      return;
    }

    const offset = this.photoDragOffset;
    this.isPhotoDragging = false;
    this.photoDragOffset = 0;

    if (offset <= -PROPERTY_CARD_SWIPE_THRESHOLD_PX) {
      this.suppressCardClick();
      if (this.currentIndex < this.photoCount - 1) {
        this.beginWindowedCommit('next');
      } else {
        this.syncDotsToPhotoIndex(this.currentIndex, false);
      }
    } else if (offset >= PROPERTY_CARD_SWIPE_THRESHOLD_PX) {
      this.suppressCardClick();
      if (this.currentIndex > 0) {
        this.beginWindowedCommit('prev');
      } else {
        this.syncDotsToPhotoIndex(this.currentIndex, false);
      }
    } else {
      this.syncDotsToPhotoIndex(this.currentIndex, false);
    }

    this.cdr.markForCheck();
  }

  get isBuildingCard(): boolean {
    return this.buildingUnitCount > 1 && !!this.buildingSlug;
  }

  get isFavorited(): boolean {
    return this.authService.checkAuthenticated()
      && this.authService.isFavoriteListing(this.propertyRecord['ListingID']);
  }

  get favoriteIconPath(): string {
    return propertyCardFavoriteSvgPath(this.isFavorited);
  }

  get propertyRecord(): Record<string, unknown> {
    return this.property as Record<string, unknown>;
  }

  get normalizedPropertyType(): string {
    return propertyTypeFrom(this.propertyRecord, this.propertyType);
  }

  get photoCount(): number {
    return this.imagePaths.length;
  }

  get imagePaths(): string[] {
    return propertyCardImagePaths(this.propertyRecord);
  }

  get currentImageUrl(): string {
    return this.imagePaths[this.currentIndex] ?? '';
  }

  get dotIndices(): number[] {
    return Array.from({ length: this.dotAnimator?.dotCount ?? 0 }, (_, index) => index);
  }

  get statSegments(): PropertyCardStatSegment[] {
    if (this.isBuildingCard) {
      return [
        {
          value: String(this.buildingUnitCount),
          label: this.buildingUnitCount === 1 ? 'unit' : 'units',
        },
      ];
    }
    return buildPropertyStatsSegments(this.propertyRecord, this.normalizedPropertyType);
  }

  get locationLabel(): string {
    return formatPropertyLocationLine(this.propertyRecord);
  }

  get priceLabel(): string {
    const price = formatPropertyPrice(this.propertyRecord['Price']);
    if (this.isBuildingCard && price) {
      return `From ${price}`;
    }
    return price;
  }

  get streetAddress(): string {
    return formatPropertyStreet(this.propertyRecord);
  }

  get statusBadge(): ListingStatusBadge | null {
    if (this.isBuildingCard) {
      return null;
    }
    return resolveListingStatusBadge(this.propertyRecord);
  }

  get activeDot(): number {
    return this.dotAnimator?.activeDot ?? 0;
  }

  get pulseDot(): number | null {
    return this.dotAnimator?.pulseDot ?? null;
  }

  showNextPhoto(event?: Event): void {
    event?.stopPropagation();
    this.changePhoto(this.currentIndex + 1);
  }

  showPrevPhoto(event?: Event): void {
    event?.stopPropagation();
    this.changePhoto(this.currentIndex - 1);
  }

  private queueCenterSlidePreloadCheck(): void {
    this.clearSnapFinalizeTimer();

    requestAnimationFrame(() => {
      const centerImg = this.getCenterSlideImage();
      if (centerImg?.complete) {
        this.finalizeWindowedSnap();
        return;
      }

      this.snapFinalizeTimer = setTimeout(() => {
        this.finalizeWindowedSnap();
      }, 120);
    });
  }

  private finalizeWindowedSnap(): void {
    if (this.centerSlidePreloadIndex === null || this.pendingPhotoIndex === null) {
      return;
    }

    this.clearSnapFinalizeTimer();

    const nextIndex = this.pendingPhotoIndex;

    this.centerSlidePreloadIndex = null;
    this.currentIndex = nextIndex;
    this.commitTrackPosition = null;
    this.pendingPhotoIndex = null;
    this.pendingPreviousPhotoIndex = null;
    this.syncDotsToPhotoIndex(this.currentIndex, false);
    this.cdr.markForCheck();

    requestAnimationFrame(() => {
      this.snapWithoutTransition = false;
      this.cdr.markForCheck();
    });
  }

  private getCenterSlideImage(): HTMLImageElement | undefined {
    // Always the middle slot (prev | current | next). Do not index into
    // querySelectorAll('.property-card-photo') — when prev is empty, only two
    // images exist and imgs[1] would be the next slide, not center.
    return this.photoTrack?.nativeElement?.querySelector(
      '.property-card-photo-slot:nth-child(2) .property-card-photo',
    ) as HTMLImageElement | undefined;
  }

  private clearSnapFinalizeTimer(): void {
    if (this.snapFinalizeTimer) {
      clearTimeout(this.snapFinalizeTimer);
      this.snapFinalizeTimer = undefined;
    }
  }

  private beginWindowedCommit(direction: 'next' | 'prev'): void {
    const previousPhotoIndex = this.currentIndex;
    const nextPhotoIndex =
      direction === 'next' ? previousPhotoIndex + 1 : previousPhotoIndex - 1;

    this.pendingPhotoIndex = nextPhotoIndex;
    this.pendingPreviousPhotoIndex = previousPhotoIndex;
    this.commitTrackPosition = direction === 'next' ? 'next' : 'prev';
    this.syncDotsToPhotoIndex(nextPhotoIndex, true, previousPhotoIndex);
    this.cdr.markForCheck();
  }

  private changePhoto(nextIndex: number): void {
    if (this.photoCount <= 1 || !this.dotAnimator) {
      return;
    }

    const previousPhotoIndex = this.currentIndex;
    const nextPhotoIndex = wrapPropertyPhotoIndex(nextIndex, this.photoCount);
    if (previousPhotoIndex === nextPhotoIndex) {
      return;
    }

    this.currentIndex = nextPhotoIndex;
    this.commitTrackPosition = null;
    this.pendingPhotoIndex = null;
    this.pendingPreviousPhotoIndex = null;
    this.syncDotsToPhotoIndex(this.currentIndex, true, previousPhotoIndex);
    this.cdr.markForCheck();
  }

  private suppressCardClick(): void {
    this.suppressClickUntil = Date.now() + 400;
  }

  private getDragPreviewPhotoIndex(): number {
    const viewportWidth = this.photoViewport?.nativeElement?.clientWidth ?? 0;
    if (viewportWidth <= 0) {
      if (this.photoDragOffset <= -PROPERTY_CARD_SWIPE_THRESHOLD_PX) {
        return wrapPropertyPhotoIndex(this.currentIndex + 1, this.photoCount);
      }

      if (this.photoDragOffset >= PROPERTY_CARD_SWIPE_THRESHOLD_PX) {
        return wrapPropertyPhotoIndex(this.currentIndex - 1, this.photoCount);
      }

      return this.currentIndex;
    }

    const progress = this.photoDragOffset / viewportWidth;
    if (progress <= -0.2) {
      return wrapPropertyPhotoIndex(this.currentIndex + 1, this.photoCount);
    }

    if (progress >= 0.2) {
      return wrapPropertyPhotoIndex(this.currentIndex - 1, this.photoCount);
    }

    return this.currentIndex;
  }

  private syncDotsToPhotoIndex(photoIndex: number, animate: boolean, previousPhotoIndex?: number): void {
    if (!this.dotAnimator || this.photoCount <= 1) {
      return;
    }

    this.dotAnimator.applyTransition(
      animate ? (previousPhotoIndex ?? null) : null,
      photoIndex,
      this.photoCount,
      animate,
    );
  }

  async toggleFavorite(event: Event): Promise<void> {
    event.stopPropagation();

    if (!this.authService.checkAuthenticated()) {
      this.loginPopup.triggerLogin();
      return;
    }

    const listingId = String(this.propertyRecord['ListingID'] ?? '');
    const nextFavorite = !this.isFavorited;
    if (nextFavorite) {
      this.authService.addFavoriteListing(listingId);
    } else {
      this.authService.removeFavoriteListing(listingId);
    }

    this.propertyRecord['favorite'] = nextFavorite;
    this.authService.postFavorite();
    this.favoriteToggled.emit({ listingId, favorite: nextFavorite });
    this.cdr.markForCheck();
  }

  onCardClick(): void {
    if (Date.now() < this.suppressClickUntil) {
      return;
    }

    if (this.isBuildingCard) {
      void this.router.navigate([this.buildingRouteBase, this.buildingSlug], {
        queryParamsHandling: 'preserve',
      });
      return;
    }

    const navigation = buildListingNavigation(this.propertyRecord, this.route, {
      returnUrl: this.returnUrl,
    });
    void this.router.navigate(navigation.commands, navigation.extras);
  }
}